import { getIntlFormaTime } from '../../common/utils';
import type { StateOfCharge } from '../../store/charging/chargingSlice';
import {
    Features,
    type EvSettings,
    type PlugType,
    type TourResponse,
    type TunnelCategory,
} from '../../store/facade/facadeApiV3';
import type { RouteConstraints, RouteCosts, RouteState } from '../../store/route/routeSlice';
import { defaultVehicleProfile } from '../../store/vehicleProfile/defaultVehicleProfile';
import type { VehicleProfile } from '../../store/vehicleProfile/vehicleProfileSlice';
import { HazardClass } from '../../store/vehicleProfile/vehicleProfileTypes';

const mapTourDeparture = (tour: TourResponse, current: RouteState): RouteState['departure'] => {
    const desiredDepartureTime = tour.departure.desiredDepartureTime;
    if (!desiredDepartureTime) {
        return current.departure;
    }

    const date = new Date(desiredDepartureTime);
    if (Number.isNaN(date.getTime())) {
        return current.departure;
    }

    return {
        departureDate: date.toISOString(),
        departureTime: getIntlFormaTime('en-US', date),
    };
};

const mapTourConstraints = (tour: TourResponse): RouteConstraints => {
    const features = tour.constraints?.features;
    return {
        isAvoidAccessHighway: features?.includes(Features.ControlledHighway) ?? false,
        isAvoidTollRoads: features?.includes(Features.TollRoad) ?? false,
        isAvoidFerry: features?.includes(Features.Ferry) ?? false,
        isAvoidTunnel: features?.includes(Features.Tunnel) ?? false,
    };
};

const mapVehicleCosts = (tour: TourResponse): RouteCosts => ({
    value: tour.costSettings?.vehiclePerKm ?? defaultVehicleProfile.costPerKm,
    currency: tour.costSettings?.currency ?? defaultVehicleProfile.currency,
});

const mapEnergyCosts = (tour: TourResponse): RouteCosts => ({
    value: tour.costSettings?.energyPerKwh ?? defaultVehicleProfile.costPerKWh,
    currency: tour.costSettings?.currency ?? defaultVehicleProfile.currency,
});

export const mapTourToRouteState = (tour: TourResponse, current: RouteState): RouteState => {
    return {
        departure: mapTourDeparture(tour, current),
        constraints: mapTourConstraints(tour),
        vehicleCosts: mapVehicleCosts(tour),
        energyCosts: mapEnergyCosts(tour),
    };
};

export interface VehicleProfileTourFields {
    cargoWeight: number;
    averageOutdoorTemperature: number;
    hazardClassification: HazardClass;
    tunnelCategory: TunnelCategory;
    plugType: PlugType;
    batteryPacks?: number;
}

const mapTourHazardClassification = (tour: TourResponse): HazardClass => {
    const hazardousClasses = tour.constraints?.hazardous_classes;
    const firstHazardClass = hazardousClasses?.at(0);
    if (!firstHazardClass) {
        return HazardClass.NONE;
    }

    return firstHazardClass as unknown as HazardClass;
};

export const mapTourToVehicleProfileFields = (
    tour: TourResponse,
    current: VehicleProfile
): VehicleProfileTourFields => {
    return {
        cargoWeight: tour.departure.initialCargoWeight,
        averageOutdoorTemperature:
            tour.departure.temperatureForecast ??
            current.averageOutdoorTemperature.value ??
            defaultVehicleProfile.averageOutdoorTemperature,
        hazardClassification: mapTourHazardClassification(tour),
        tunnelCategory: (tour.vehicle.tunnelCategory ?? current.tunnelCategory.value) as TunnelCategory,
        plugType: (tour.vehicle.plugType?.at(0) ?? current.plugType.value) as PlugType,
        batteryPacks: tour.vehicle.batteryCount ?? current.batteryPacks.value,
    };
};

const isFiniteNumber = (value: number | undefined): value is number =>
    typeof value === 'number' && Number.isFinite(value);

const safePercentage = (absolute: number | undefined, total: number | undefined, fallback: number): number => {
    if (!isFiniteNumber(total) || total <= 0 || !isFiniteNumber(absolute)) {
        return fallback;
    }

    const pct = (absolute / total) * 100;
    if (!Number.isFinite(pct)) {
        return fallback;
    }

    return Math.min(100, Math.max(0, pct));
};

export const mapEvSettingsToStateOfCharge = (evSettings: EvSettings | undefined): StateOfCharge => {
    return {
        initSoC: safePercentage(
            evSettings?.initialBatteryCapacity,
            evSettings?.totalBatteryCapacity,
            defaultVehicleProfile.initSoC
        ),
        minSoC: safePercentage(
            evSettings?.minBatteryCapacity,
            evSettings?.totalBatteryCapacity,
            defaultVehicleProfile.minSoC
        ),
        maxSoC: safePercentage(
            evSettings?.maxBatteryCapacity,
            evSettings?.totalBatteryCapacity,
            defaultVehicleProfile.maxSoC
        ),
        destinationSoC: safePercentage(
            evSettings?.minBatteryCapacityAtDestination,
            evSettings?.totalBatteryCapacity,
            defaultVehicleProfile.destinationSoC
        ),
    };
};
